/*
 * Healix Premade Set — controller for the `product.set` template.
 *
 * A "Set" is a non-surfacing shell product whose page composes a paired TOP and
 * PANT into a single buy box. The shell carries two anchor colourway handles in
 * metafields (custom.set_top / custom.set_bottom); the block prints them onto
 * the root element as data-top-handle / data-pant-handle.
 *
 * In this store every colourway is its OWN product, so "shared colour" means:
 *   1. Load the family siblings for BOTH anchors (HealixColors.siblingsRaw).
 *   2. Intersect by colour slug -> the colours available in BOTH the top and the
 *      pant family. That intersection is the ONE shared colour strip.
 *   3. Picking a colour resolves the specific top colourway product + pant
 *      colourway product for that slug.
 *   4. Each piece then gets its OWN size picker (pants also a length picker),
 *      chosen INDEPENDENTLY, rendered in the PDP variant-picker markup
 *      (.product-variants-block__pill etc.) so it is visually identical to the
 *      live PDP and tracks future PDP style changes automatically. Variant data
 *      comes from /products/<handle>.js (HealixColors.product, cached).
 *   5. "Add set to bag" resolves the two variant ids and adds BOTH as separate
 *      line items via window.CartAPI.addItems([...]).
 *
 * Per-component chrome the ghost can't supply (product card + size guide) is
 * SERVER-rendered from the anchor products (blocks/product-set-buy.liquid); this
 * module keeps the card image/title/href in sync on colour change.
 *
 * No-default selection (mirrors the PDP): nothing is preselected; attempting to
 * add with an incomplete selection highlights ONLY the FIRST unselected field
 * (top -> pant, option order) with the PDP's red-pulse prompt and scrolls it
 * into view — subsequent clicks move to the next gap; a field's highlight clears
 * the moment it's filled.
 *
 * Set price = SUM of the two components' currently-relevant variant prices
 * (compare-at aware), rendered in the PDP price-block markup.
 *
 * Skeleton loading states cover every async moment (boot colour strip + cards,
 * per-piece picker rows on colour change) so nothing pops in with a layout jump.
 *
 * Self-contained: depends only on HealixColors (assets/healix-colors.js),
 * CartAPI (assets/cart-api.js) and window.formatMoney — all already shipped.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Utilities                                                           */
  /* ------------------------------------------------------------------ */

  function money(cents) {
    if (typeof window.formatMoney === 'function') {
      try { return window.formatMoney(cents, window.themeMoneyFormat); } catch (e) { /* fall through */ }
    }
    if (typeof window.formatMoneyFallback === 'function') {
      try { return window.formatMoneyFallback(cents); } catch (e) { /* fall through */ }
    }
    return '$' + ((Number(cents) || 0) / 100).toFixed(2);
  }

  function sizedImage(url, size) {
    if (!url) return '';
    return String(url).replace(/(\.(?:jpg|jpeg|png|gif|webp))(\?.*)?$/i, '_' + size + '$1$2');
  }

  function el(tag, cls, attrs) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (attrs) Object.keys(attrs).forEach(function (k) { e.setAttribute(k, attrs[k]); });
    return e;
  }

  function paintIcons(scope) {
    if (window.UntamedIcons && window.UntamedIcons.refresh) window.UntamedIcons.refresh(scope);
  }

  // Perceived luminance (WCAG-ish) → white vs dark chip text on the hex.
  // Mirrors tagTextColor in assets/collection-family-sections.js so the set-PDP
  // colour chip matches the product-card colour chip exactly.
  function tagTextColor(hex) {
    var h = String(hex || '').replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    if (h.length !== 6) return '#FFFFFF';
    var r = parseInt(h.slice(0, 2), 16) / 255;
    var g = parseInt(h.slice(2, 4), 16) / 255;
    var b = parseInt(h.slice(4, 6), 16) / 255;
    var lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return lum > 0.6 ? '#1D2129' : '#FFFFFF';
  }

  // Build the selected-colour hex CHIP using the theme tag component's
  // classes/inline-style, identical to makeTag in
  // assets/collection-family-sections.js (product-card colour chip):
  // .tag.tag--mini.tag--solid.tag--hex with --tag-hex / --tag-hex-text inline.
  function makeColorChip(name, hex, slug) {
    var tag = el('span', 'tag tag--mini tag--solid product-card__badge' + (hex ? ' tag--hex' : ' tag--default'));
    if (hex) {
      tag.style.setProperty('--tag-hex', hex);
      tag.style.setProperty('--tag-hex-text', tagTextColor(hex));
    }
    if (slug) tag.setAttribute('data-color-slug', slug);
    var txt = el('span', 'tag__text');
    txt.textContent = name;
    tag.appendChild(txt);
    return tag;
  }

  /* ------------------------------------------------------------------ */
  /* Shareable URL state (Figs-style deep links)                         */
  /*                                                                     */
  /* A set link can carry the selected shared colour and each piece's    */
  /* size/length picks so it re-opens on the same selection:             */
  /*   ?color=<slug>&top_size=M&pant_size=S&pant_length=Petite           */
  /* Values are the human-readable option strings, NOT variant ids.      */
  /*                                                                     */
  /* Precedent: the normal PDP writes ?variant= via history.replaceState */
  /* (assets/product.js); the family view uses a ?family= deep link. We  */
  /* mirror that — replaceState only (no history spam / no popstate), and*/
  /* we ONLY touch our own params so unrelated ones (preview_theme_id,   */
  /* utm_*, …) and the hash survive untouched.                           */

  var COLOR_PARAM = 'color';

  // Per-piece option param name: "<piece>_<option-name-lowercased>",
  // URL-safe. Only multi-value options get a param (single-value options
  // are auto-resolved + hidden, so there's nothing to share). Derived
  // generically from product.options so new option names just work.
  function pieceParamName(pieceKey, optName) {
    return pieceKey + '_' + String(optName || '').trim().toLowerCase().replace(/\s+/g, '-');
  }

  // The full set of param keys THIS module manages, given the current
  // pieces. Used to strip stale params before re-writing (so size params
  // vanish when a colour change reloads a piece with different options).
  function managedParamKeys(pieces, order) {
    var keys = [COLOR_PARAM];
    order.forEach(function (pk) {
      var piece = pieces[pk];
      if (!piece.product || !piece.product.options) return;
      piece.product.options.forEach(function (opt) {
        if (!opt.values || opt.values.length <= 1) return;
        keys.push(pieceParamName(pk, opt.name));
      });
    });
    return keys;
  }

  // Snapshot the query params at boot (for deep-link reads). URLSearchParams is
  // supported everywhere this theme runs (it's already used across assets).
  function readUrlParams() {
    var out = {};
    try {
      new URL(window.location.href).searchParams.forEach(function (v, k) { out[k] = v; });
    } catch (e) { /* leave empty on malformed URLs — degrade gracefully */ }
    return out;
  }

  // Reflect current selection into the URL via replaceState, preserving all
  // unrelated params (preview_theme_id, utm_*, …) and the hash. `desired` is a
  // map of OUR params to set; every managed key not present in `desired` is
  // removed (stale size params drop off when a piece reloads).
  function writeUrlState(managedKeys, desired) {
    if (!window.history || !window.history.replaceState) return;
    var url;
    try { url = new URL(window.location.href); } catch (e) { return; }
    var params = url.searchParams;
    // Drop every managed key first so removed selections don't linger.
    managedKeys.forEach(function (k) { params.delete(k); });
    Object.keys(desired).forEach(function (k) {
      var val = desired[k];
      if (val != null && val !== '') params.set(k, val);
    });
    try { window.history.replaceState(window.history.state, '', url.toString()); } catch (e) { /* no-op */ }
  }

  // Helper text under a Length pill, mirroring the PDP's hard-coded copy
  // (blocks/product-variants.liquid — Length pill subtext).
  function lengthSubtext(value) {
    switch (String(value || '').toLowerCase()) {
      case 'petite': return '29" - Up to 5\'5"';
      case 'regular': return '31" - 5\'6" to 5\'8"';
      case 'tall': return '33" - 5\'9" and above';
      default: return '';
    }
  }

  // Variant resolution mirrors healix-variant-sheet.js so availability + picking
  // feel identical across the two surfaces.
  function valueAvailable(product, chosen, optIdx, value) {
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      if (!v.available) continue;
      if (String(v.options[optIdx]) !== String(value)) continue;
      var ok = true;
      for (var k = 0; k < product.options.length; k++) {
        if (k === optIdx) continue;
        if (chosen[k] != null && String(v.options[k]) !== String(chosen[k])) { ok = false; break; }
      }
      if (ok) return true;
    }
    return false;
  }

  function resolveVariant(product, chosen) {
    for (var k = 0; k < chosen.length; k++) { if (chosen[k] == null) return null; }
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i], ok = true;
      for (var j = 0; j < chosen.length; j++) {
        if (String(v.options[j]) !== String(chosen[j])) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  // First AVAILABLE variant (fallback: first) — the "currently relevant" variant
  // for pricing before a size is picked, matching the PDP's
  // selected_or_first_available_variant.
  function firstRelevantVariant(product) {
    if (!product || !product.variants || !product.variants.length) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) return product.variants[i];
    }
    return product.variants[0];
  }

  /* ------------------------------------------------------------------ */
  /* Controller                                                          */
  /* ------------------------------------------------------------------ */

  function initSet(root) {
    if (!root || root.dataset.hxsetReady === 'true') return;
    if (!window.HealixColors || !window.HealixColors.siblingsRaw || !window.HealixColors.product) return;
    root.dataset.hxsetReady = 'true';

    var topHandle = (root.getAttribute('data-top-handle') || '').trim();
    var pantHandle = (root.getAttribute('data-pant-handle') || '').trim();
    var addLabel = root.getAttribute('data-add-label') || 'Add set to bag';
    if (!topHandle || !pantHandle) { root.hidden = true; return; }

    var dom = {
      colorways: root.querySelector('[data-hxset-colorways]'),
      colorMount: root.querySelector('[data-hxset-color-mount]'),
      colorName: root.querySelector('[data-hxset-color-name]'),
      colorSkeleton: root.querySelector('[data-hxset-colorways-skeleton]'),
      price: root.querySelector('[data-hxset-price]'),
      priceSkeleton: root.querySelector('[data-hxset-price-skeleton]'),
      priceCurrent: root.querySelector('[data-hxset-price-current]'),
      priceCompare: root.querySelector('[data-hxset-price-compare]'),
      add: root.querySelector('[data-hxset-add]'),
      addText: root.querySelector('[data-hxset-add-text]'),
      error: root.querySelector('[data-hxset-error]'),
      switcher: root.querySelector('[data-hxset-set-switcher]')
    };

    // pieces[key] = { key, label, anchor, colorHandle, product, chosen[], variant,
    //                 pickersHost, card, cardImg, cardTitle, cardLinks[] }
    // Per-piece DOM is server-rendered inside <section data-hxset-piece="KEY">:
    // component card, the size-guide region (.hxset__size-guide with its own
    // [data-size-chart-link] trigger), then the empty pickers mount this module
    // fills. Scoping every lookup to `section` is what keeps each piece's own
    // size-guide trigger anchored to ITS OWN Size row (see placePieceSizeGuide).
    var topSection = root.querySelector('[data-hxset-piece="top"]');
    var pantSection = root.querySelector('[data-hxset-piece="pant"]');
    var pieces = {
      top: {
        key: 'top', label: 'Top', anchor: topHandle,
        section: topSection,
        pickersHost: topSection && topSection.querySelector('[data-hxset-piece-pickers="top"]'),
        card: topSection && topSection.querySelector('[data-hxset-card="top"]')
      },
      pant: {
        // user-facing "Bottom"; historical metafield/JS key stays "pant".
        key: 'pant', label: 'Bottom', anchor: pantHandle,
        section: pantSection,
        pickersHost: pantSection && pantSection.querySelector('[data-hxset-piece-pickers="pant"]'),
        card: pantSection && pantSection.querySelector('[data-hxset-card="pant"]')
      }
    };
    var order = ['top', 'pant'];
    var shared = [];        // [{ slug, name, hex, edition, top:{handle,imageUrl}, pant:{handle,imageUrl} }]
    var activeSlug = null;
    var adding = false;
    // Deep-linked size/length params only auto-apply on the INITIAL load; a
    // later manual colour change resets sizes (existing behaviour) and must NOT
    // re-pull URL values. Consumed once, after boot the URL is write-only.
    var bootParams = readUrlParams();
    var deepLinkPending = true;
    // syncUrl is a no-op until the first colour is selected at boot, so the
    // early updateState() (empty selection) can't strip the deep-link params
    // before we've read them.
    var urlSyncArmed = false;

    function setError(msg) {
      if (!dom.error) return;
      if (msg) { dom.error.textContent = msg; dom.error.hidden = false; }
      else { dom.error.hidden = true; dom.error.textContent = ''; }
    }

    /* ---- shared colour strip ------------------------------------------- */

    function indexSiblings(sib) {
      // slug -> { name, hex, edition, handle, imageUrl, order }
      var map = {};
      var order = 0;
      ['core', 'limited'].forEach(function (group) {
        (sib[group] || []).forEach(function (c) {
          var slug = String(c.color || '').toLowerCase();
          if (!slug || map[slug]) return;
          map[slug] = {
            name: c.colorName || c.color || 'Colour',
            hex: c.hex || '',
            edition: group,
            handle: c.handle,
            imageUrl: c.imageUrl || '',
            order: order++
          };
        });
      });
      return map;
    }

    function buildShared(topSib, pantSib) {
      var t = indexSiblings(topSib);
      var p = indexSiblings(pantSib);
      var out = [];
      Object.keys(t).forEach(function (slug) {
        if (!p[slug]) return;
        var te = t[slug], pe = p[slug];
        out.push({
          slug: slug,
          name: te.name,
          hex: te.hex || pe.hex,
          edition: (te.edition === 'limited' && pe.edition === 'limited') ? 'limited' : 'core',
          order: te.order,
          top: { handle: te.handle, imageUrl: te.imageUrl },
          pant: { handle: pe.handle, imageUrl: pe.imageUrl }
        });
      });
      out.sort(function (a, b) { return a.order - b.order; });
      return out;
    }

    function makeSwatch(color) {
      var isActive = color.slug === activeSlug;
      var btn = el('button', 'cfs-swatch' + (isActive ? ' is-active' : ''), {
        type: 'button',
        title: color.name,
        'aria-label': color.name,
        'data-color-slug': color.slug
      });
      if (isActive) btn.setAttribute('aria-current', 'true');
      var chip = el('span', 'cfs-swatch__chip');
      if (color.hex) chip.style.backgroundColor = color.hex;
      btn.appendChild(chip);
      btn.addEventListener('click', function () { selectColor(color.slug); });
      return btn;
    }

    function renderColorStrip() {
      if (!dom.colorMount) return;
      dom.colorMount.textContent = '';
      var groups = [
        { label: 'Core', items: shared.filter(function (c) { return c.edition !== 'limited'; }) },
        { label: 'Limited', items: shared.filter(function (c) { return c.edition === 'limited'; }) }
      ];
      groups.forEach(function (g) {
        if (!g.items.length) return;
        var group = el('div', 'cfs-swatches__group');
        var label = el('span', 'cfs-swatches__label');
        label.textContent = g.label;
        group.appendChild(label);
        var dots = el('div', 'cfs-swatches__dots');
        g.items.forEach(function (c) { dots.appendChild(makeSwatch(c)); });
        group.appendChild(dots);
        dom.colorMount.appendChild(group);
      });
      if (dom.colorways) dom.colorways.hidden = false;
      // Boot colour-strip skeleton has served its purpose.
      if (dom.colorSkeleton) dom.colorSkeleton.hidden = true;
    }

    /* ---- piece pickers (PDP-parity markup) ----------------------------- */

    // Skeleton picker rows shaped like the real PDP-parity pickers, shown while
    // /products/<handle>.js resolves on a colour change.
    function renderPieceSkeleton(piece) {
      piece.pickersHost.textContent = '';
      // Two placeholder rows (size + possible length) — pill blocks.
      [4, 3].forEach(function (count, ri) {
        var row = el('div', 'hxset__sk-picker');
        row.appendChild(el('span', 'hxset__sk hxset__sk-picker__label'));
        var pills = el('div', 'hxset__sk-picker__pills');
        for (var i = 0; i < count; i++) {
          pills.appendChild(el('span', 'hxset__sk hxset__sk-pill' + (ri === 1 ? ' hxset__sk-pill--length' : '')));
        }
        row.appendChild(pills);
        piece.pickersHost.appendChild(row);
      });
    }

    function renderPieceEmpty(piece) {
      if (!piece.pickersHost) return;
      piece.pickersHost.innerHTML =
        '<p class="hxset__await">Choose a colour to pick a size.</p>';
    }

    // Emit the PDP variant-picker markup for each multi-value option so the set
    // pickers are visually identical to the live PDP. Single-value options
    // (e.g. Length: REGULAR) are auto-resolved + hidden, exactly as the PDP does.
    function renderPiecePickers(piece) {
      var product = piece.product;
      piece.pickersHost.textContent = '';
      product.options.forEach(function (opt, oi) {
        var values = opt.values || [];
        if (values.length <= 1) {
          if (values.length === 1) piece.chosen[oi] = values[0];
          return;
        }
        var isLength = String(opt.name || '').toLowerCase() === 'length';

        var option = el('div', 'product-variants-block__option', {
          'data-option-position': oi + 1,
          'data-hxset-opt': oi,
          'data-option-deferred': ''
        });

        var label = el('label', 'product-variants-block__label');
        label.appendChild(document.createTextNode(opt.name));
        var selVal = el('span', 'product-variants-block__selected-value');
        label.appendChild(selVal);
        option.appendChild(label);

        var pills = el('div', 'product-variants-block__pills' + (isLength ? ' product-variants-block__pills--length' : ''));
        values.forEach(function (val) {
          var pill = el('button', 'product-variants-block__pill' + (isLength ? ' product-variants-block__pill--length' : ''), {
            type: 'button',
            'data-option-name': opt.name,
            'data-option-value': val
          });
          if (isLength) {
            var main = el('span', 'product-variants-block__pill-main');
            main.textContent = val;
            pill.appendChild(main);
            var sub = el('small', 'product-variants-block__pill-subtext');
            sub.textContent = lengthSubtext(val);
            pill.appendChild(sub);
          } else {
            pill.textContent = val;
          }
          pill.addEventListener('click', function () {
            if (pill.disabled) return;
            piece.chosen[oi] = (String(piece.chosen[oi]) === String(val)) ? null : val;
            clearPromptFor(option);
            refreshPiece(piece);
            updateState();
          });
          pills.appendChild(pill);
        });
        option.appendChild(pills);
        option._selVal = selVal;
        piece.pickersHost.appendChild(option);
      });
      refreshPiece(piece);
      placePieceSizeGuide(piece);
    }

    // Anchor THIS piece's size-guide trigger to ITS OWN Size option header row.
    //
    // Why this exists: size-chart.js auto-boots every [data-size-chart-root] and
    // tries to relocate each region's link into the Size option's label — but it
    // finds that Size option with a GLOBAL document.querySelector, which always
    // returns the FIRST Size option on the page. With two pieces that means BOTH
    // guide links land in the TOP piece's Size row (side by side) and the bottom
    // piece gets none — the reported bug. size-chart.js also runs at boot, before
    // these JS-rendered pickers exist, so its relocation no-ops then anyway.
    //
    // We render the pickers LAST (async, after HealixColors resolves), so this is
    // the authoritative placement: find the guide link WITHIN this piece's own
    // <section> and move it into this piece's own Size option label. Robust to JS
    // render order and to whatever size-chart.js did — each component ends up with
    // exactly one, correctly-categorised guide link on its own Size header row.
    //
    // Reuses the PDP header pattern verbatim: the link becomes the last child of
    // .product-variants-block__label, and size-chart.css's
    // `.product-variants-block__label .product-size-chart-link { margin-left:auto }`
    // lays it out flush-right on the same baseline as the "Size" label — the exact
    // space-between header row the live PDP produces (no bespoke layout invented).
    function placePieceSizeGuide(piece) {
      if (!piece.section || !piece.pickersHost) return;
      var link = piece.section.querySelector('[data-size-chart-link]');
      if (!link) return; // no chart resolved for this piece — nothing to place
      // The Size option specifically (Length rows get no guide link).
      var sizeOption = null;
      piece.pickersHost.querySelectorAll('[data-hxset-opt]').forEach(function (opt) {
        if (sizeOption) return;
        var pill = opt.querySelector('[data-option-name]');
        var name = pill && pill.getAttribute('data-option-name');
        if (name && String(name).toLowerCase() === 'size') sizeOption = opt;
      });
      if (!sizeOption) return; // no multi-value Size option (single size auto-resolved)
      var label = sizeOption.querySelector('.product-variants-block__label');
      if (label && link.parentNode !== label) label.appendChild(link);
    }

    // Repaint availability + selected state (PDP classes: .active, disabled).
    function refreshPiece(piece) {
      var product = piece.product;
      piece.pickersHost.querySelectorAll('[data-hxset-opt]').forEach(function (option) {
        var oi = parseInt(option.getAttribute('data-hxset-opt'), 10);
        var chosen = piece.chosen[oi];
        option.querySelectorAll('.product-variants-block__pill').forEach(function (pill) {
          var val = pill.getAttribute('data-option-value');
          var available = valueAvailable(product, piece.chosen, oi, val);
          var selected = chosen != null && String(chosen) === String(val);
          pill.disabled = !available;
          pill.classList.toggle('active', selected);
          pill.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        if (option._selVal) option._selVal.textContent = chosen != null ? ' ' + chosen : '';
      });
    }

    /* ---- component cards ----------------------------------------------- */

    // Keep the purpose-built component card (.hxset__component-card, markup owned
    // by blocks/product-set-buy.liquid) in sync with the selected colour: image
    // from the event-detail imageUrl IMMEDIATELY (no blanking — swap src in place,
    // and if the boot placeholder was a skeleton span, replace it with a real
    // <img>), then title + href once the colourway product resolves. The role
    // eyebrow (TOP / BOTTOM) is fixed markup — it identifies the SLOT, not the
    // product, so it never changes on colour switch.
    function updateCardImage(piece, url) {
      if (!piece.card || !url) return;
      var slot = piece.card.querySelector('[data-hxset-card-img]');
      if (!slot) return;
      var sized = sizedImage(url, '240x300');
      if (slot.tagName === 'IMG') {
        slot.src = sized;
        slot.removeAttribute('srcset');
      } else {
        // Boot skeleton placeholder — upgrade it to a real image in place.
        var img = el('img', 'hxset__component-card__img', {
          'data-hxset-card-img': '',
          src: sized,
          alt: '',
          width: '64',
          height: '80',
          loading: 'lazy'
        });
        slot.parentNode.replaceChild(img, slot);
      }
    }

    function updateCardProduct(piece, product) {
      if (!piece.card || !product) return;
      var titleEl = piece.card.querySelector('[data-hxset-card-title]');
      if (titleEl && product.title) {
        titleEl.textContent = product.title;
        // Shed the boot skeleton treatment once the real title lands.
        titleEl.classList.remove('hxset__sk', 'hxset__sk-line');
        titleEl.removeAttribute('style');
      }
      var href = product.url || ('/products/' + product.handle);
      piece.card.querySelectorAll('[data-hxset-card-link]').forEach(function (a) {
        a.setAttribute('href', href);
      });
      var featured = product.featured_image || (product.images && product.images[0]);
      if (featured) updateCardImage(piece, featured);
    }

    /* ---- colour selection --------------------------------------------- */

    // Keep the set-switcher pills deep-linking into the currently selected
    // colour, so switching sets lands on the same colour preselected (the
    // ?color= boot logic falls back gracefully if that set lacks it).
    function syncSwitcherLinks(slug) {
      if (!dom.switcher) return;
      dom.switcher.querySelectorAll('a.hxset__set-pill').forEach(function (a) {
        try {
          var url = new URL(a.getAttribute('href'), window.location.origin);
          url.searchParams.set('color', slug);
          a.setAttribute('href', url.pathname + url.search);
        } catch (e) { /* unparseable href — leave the plain link */ }
      });
    }

    function selectColor(slug) {
      var color = null;
      for (var i = 0; i < shared.length; i++) { if (shared[i].slug === slug) { color = shared[i]; break; } }
      if (!color) return;
      activeSlug = slug;
      syncSwitcherLinks(slug);

      dom.colorMount.querySelectorAll('.cfs-swatch').forEach(function (s) {
        var on = s.getAttribute('data-color-slug') === slug;
        s.classList.toggle('is-active', on);
        if (on) s.setAttribute('aria-current', 'true'); else s.removeAttribute('aria-current');
      });
      if (dom.colorName) {
        dom.colorName.textContent = '';
        dom.colorName.appendChild(makeColorChip(color.name, color.hex, color.slug));
      }

      setError('');

      // Reset both pieces to loading; show picker skeletons + update card images
      // immediately from the event-detail imageUrl (keep the old card visible —
      // skeleton is only the boot-time no-content case).
      order.forEach(function (key) {
        var piece = pieces[key];
        piece.colorHandle = color[key].handle;
        piece.imageUrl = color[key].imageUrl;
        piece.product = null;
        piece.chosen = null;
        piece.variant = null;
        if (piece.pickersHost) renderPieceSkeleton(piece);
        if (piece.imageUrl) updateCardImage(piece, piece.imageUrl);
      });

      // Event contract for the set-aware media gallery (fires on EVERY colour
      // selection incl. the automatic shared[0] pick at boot).
      document.dispatchEvent(new CustomEvent('healix:set:color-change', {
        detail: {
          slug: slug,
          hex: color.hex,
          name: color.name,
          top: { handle: pieces.top.colorHandle, imageUrl: pieces.top.imageUrl },
          pant: { handle: pieces.pant.colorHandle, imageUrl: pieces.pant.imageUrl }
        }
      }));

      updateState();

      var pendingResolves = order.length;
      order.forEach(function (key) {
        var piece = pieces[key];
        var wanted = piece.colorHandle;
        window.HealixColors.product(wanted).then(function (product) {
          if (piece.colorHandle !== wanted) return; // superseded (stale guard — respect it)
          if (!product || !product.variants) {
            piece.pickersHost.innerHTML = '<p class="hxset__await">Couldn’t load this piece.</p>';
            return;
          }
          piece.product = product;
          piece.chosen = product.options.map(function (opt) {
            var values = opt.values || [];
            return values.length === 1 ? values[0] : null;
          });
          // Deep link: apply valid ?<piece>_<option>= sizes on the INITIAL load
          // only, BEFORE the pickers render so pills paint selected + gaps clear
          // via the same refresh path a manual pill click uses.
          applyDeepLinkSizes(piece);
          updateCardProduct(piece, product);
          renderPiecePickers(piece);
          updateState();
          // Once BOTH pieces of the first colour have resolved, the deep-link
          // window closes: later manual colour changes reset sizes and must not
          // re-pull URL values (URL becomes write-only after boot).
          if (deepLinkPending && --pendingResolves <= 0) deepLinkPending = false;
        });
      });
    }

    /* ---- price / state / add ------------------------------------------ */

    // Set price = sum of each piece's currently-relevant variant price (the
    // chosen variant once complete, else first-available). Compare-at aware:
    // per piece use compare_at_price when > price, else price; show the summed
    // compare-at strikethrough only when it exceeds the price total. Prices are
    // CENTS from the Shopify .js payload (never the colours-API dollar strings).
    // Show / hide the price skeleton reliably. The .hxset__sk shimmer rule sets
    // `display:block`, which OVERRIDES the UA-stylesheet `[hidden]{display:none}`
    // — so toggling the `hidden` attribute alone left the shimmer painted next to
    // the resolved price (the reported double-render). Drive `display` directly
    // so hide always wins, and keep the two states mutually exclusive so rapid
    // colour switches can never leave both the skeleton and the price visible.
    function setPriceSkeleton(show) {
      if (!dom.priceSkeleton) return;
      dom.priceSkeleton.hidden = !show;
      dom.priceSkeleton.style.display = show ? '' : 'none';
    }

    function updatePrice() {
      var haveBoth = pieces.top.product && pieces.pant.product;
      if (!haveBoth) {
        // Re-resolving (colour change before both pieces land): show the skeleton
        // and hide any stale resolved price so the two never co-render.
        setPriceSkeleton(true);
        if (dom.priceCurrent) dom.priceCurrent.hidden = true;
        if (dom.priceCompare) dom.priceCompare.hidden = true;
        return 0;
      }
      var priceTotal = 0, compareTotal = 0;
      order.forEach(function (key) {
        var piece = pieces[key];
        var v = (piece.chosen ? resolveVariant(piece.product, piece.chosen) : null) || firstRelevantVariant(piece.product);
        var p = v ? v.price : 0;
        var c = (v && v.compare_at_price && v.compare_at_price > p) ? v.compare_at_price : p;
        priceTotal += p;
        compareTotal += c;
      });

      // Same operation that reveals the resolved price tears down the skeleton.
      setPriceSkeleton(false);
      var onSale = compareTotal > priceTotal;
      if (dom.priceCurrent) {
        dom.priceCurrent.textContent = money(priceTotal);
        dom.priceCurrent.hidden = false;
        dom.priceCurrent.classList.toggle('product-price-block__current--on-sale', onSale);
      }
      if (dom.priceCompare) {
        if (onSale) {
          dom.priceCompare.textContent = money(compareTotal);
          dom.priceCompare.hidden = false;
        } else {
          dom.priceCompare.hidden = true;
        }
      }
      return priceTotal;
    }

    function updateState() {
      var total = updatePrice();
      var allResolved = activeSlug != null && pieces.top.product && pieces.pant.product;
      order.forEach(function (key) {
        var piece = pieces[key];
        if (!piece.product || !piece.chosen) { allResolved = false; return; }
        var v = resolveVariant(piece.product, piece.chosen);
        piece.variant = v;
        if (!v) allResolved = false;
      });

      // Add-to-bag button — PDP-parity disabled state.
      //
      // The live PDP keeps its buy button DISABLED-LOOKING while any deferred
      // option is unpicked, with the label naming the first missing option
      // ("Select a size"); once everything is chosen it becomes the normal
      // enabled add button. We mirror that here (label advances through the same
      // gap order as promptFirstGap):
      //   incomplete -> btn--disabled treatment (theme's opacity/cursor, keeps the
      //                 --btn-primary-* theming) + aria-disabled, label = first gap
      //   complete   -> enabled, label = "<add label> · <total>"
      //
      // We DON'T set the native `disabled` attribute while incomplete: a truly
      // disabled button swallows pointer events, and we still want a click to run
      // promptFirstGap (highlight + scroll to the field), exactly as the PDP's
      // clickable pending gate does. btn--disabled gives the identical disabled
      // LOOK; aria-disabled carries the semantics for assistive tech. The in-flight
      // "Adding…" state (managed by addSet) is left untouched here.
      if (!adding && dom.add) {
        var gapLabel = allResolved ? null : firstGapButtonLabel();
        var incomplete = gapLabel != null;
        dom.add.classList.toggle('btn--disabled', incomplete);
        if (incomplete) {
          dom.add.setAttribute('aria-disabled', 'true');
        } else {
          dom.add.removeAttribute('aria-disabled');
        }
        dom.add.disabled = false; // stay clickable so promptFirstGap can fire
        if (dom.addText) {
          dom.addText.textContent = incomplete
            ? gapLabel
            : addLabel + ' · ' + money(total);
        }
      } else if (dom.add) {
        // adding === true: addSet owns the label ("Adding…"/"Added…") and disables.
        dom.add.disabled = true;
      }

      syncUrl();
    }

    /* ---- shareable URL reflection -------------------------------------- */

    // Reflect the live selection (colour + each picked, shared-worthy option)
    // into the URL. Called from updateState so it runs on EVERY colour select
    // and size/length pick/un-pick. replaceState only; unrelated params + hash
    // preserved; params for options that no longer apply are dropped.
    function syncUrl() {
      if (!urlSyncArmed) return;
      var desired = {};
      if (activeSlug != null) desired[COLOR_PARAM] = activeSlug;
      order.forEach(function (key) {
        var piece = pieces[key];
        if (!piece.product || !piece.product.options || !piece.chosen) return;
        piece.product.options.forEach(function (opt, oi) {
          if (!opt.values || opt.values.length <= 1) return; // single-value: nothing to share
          var val = piece.chosen[oi];
          if (val != null && val !== '') desired[pieceParamName(key, opt.name)] = val;
        });
      });
      writeUrlState(managedParamKeys(pieces, order), desired);
    }

    // Apply valid deep-linked size/length params to a freshly resolved piece —
    // INITIAL load only (bootParams captured pre-boot; consumed by clearing
    // deepLinkPending after both pieces resolve). A value applies IFF it exists
    // for that multi-value option AND leaves an available selection per the
    // existing availability logic. Invalid/unavailable values are ignored.
    function applyDeepLinkSizes(piece) {
      if (!deepLinkPending) return;
      var product = piece.product;
      if (!product || !product.options) return;
      product.options.forEach(function (opt, oi) {
        var values = opt.values || [];
        if (values.length <= 1) return;
        var raw = bootParams[pieceParamName(piece.key, opt.name)];
        if (raw == null) return;
        // Match the option value case-insensitively; use the canonical string.
        var match = null;
        for (var i = 0; i < values.length; i++) {
          if (String(values[i]).toLowerCase() === String(raw).toLowerCase()) { match = values[i]; break; }
        }
        if (match == null) return;                                  // value doesn't exist
        if (!valueAvailable(product, piece.chosen, oi, match)) return; // unavailable in context
        piece.chosen[oi] = match;
      });
    }

    /* ---- first-gap prompt (mirrors the PDP .is-prompt flow) ------------ */

    // Ordered list of "gaps": colour first (if unpicked), then each piece's
    // unselected option in top -> pant, option order. Each gap carries the DOM
    // target to highlight/scroll and the prompt message.
    function gaps() {
      var out = [];
      if (activeSlug == null) {
        out.push({ target: dom.colorways, msg: 'Please choose a colour.' });
        return out;
      }
      order.forEach(function (key) {
        var piece = pieces[key];
        if (!piece.product || !piece.chosen) {
          out.push({ target: piece.pickersHost, msg: 'Loading ' + piece.label.toLowerCase() + '…' });
          return;
        }
        piece.product.options.forEach(function (opt, oi) {
          if (piece.chosen[oi] != null) return;
          var option = piece.pickersHost.querySelector('[data-hxset-opt="' + oi + '"]');
          out.push({
            target: option,
            msg: 'Please select a ' + piece.label.toLowerCase() + ' ' + opt.name.toLowerCase()
          });
        });
      });
      return out;
    }

    // Button label for the FIRST unmet requirement, PDP-parity phrasing.
    //
    // The live PDP add-to-cart button (assets/product.js pendingLabel) reads
    // "Select a <option>" while a deferred option is unpicked. We mirror that
    // exactly, only qualified by component since the set has two pieces:
    //   colour unpicked          -> "Select a color"
    //   piece still resolving     -> "Loading…"  (sizes unknown; button waits)
    //   piece's Size unpicked     -> "Select a top size" / "Select a bottom size"
    //   piece's Length unpicked   -> "Select a bottom length"
    // Returns null when nothing is missing (everything selected → enable + show
    // the normal add label). Ordering is identical to gaps() so the button label
    // and the promptFirstGap highlight always point at the same field.
    function firstGapButtonLabel() {
      if (activeSlug == null) return 'Select a color';
      var loading = false;
      var label = null;
      order.forEach(function (key) {
        if (label || loading) return;
        var piece = pieces[key];
        if (!piece.product || !piece.chosen) { loading = true; return; }
        piece.product.options.forEach(function (opt, oi) {
          if (label) return;
          var values = opt.values || [];
          if (values.length <= 1) return;         // single-value: auto-resolved
          if (piece.chosen[oi] != null) return;   // already picked
          label = 'Select a ' + piece.label.toLowerCase() + ' ' + String(opt.name).toLowerCase();
        });
      });
      if (label) return label;
      if (loading) return 'Loading…';
      return null;
    }

    function clearPromptFor(target) {
      if (!target) return;
      target.classList.remove('is-prompt', 'hxset__gap-target');
      var msg = target.querySelector('.hxset__prompt-msg');
      if (msg) msg.remove();
    }

    function clearAllPrompts() {
      root.querySelectorAll('.hxset__gap-target').forEach(clearPromptFor);
    }

    // Highlight ONLY the first unselected field, scroll into view if needed.
    function promptFirstGap() {
      var g = gaps();
      if (!g.length) return;
      var first = g[0];
      clearAllPrompts();
      var target = first.target;
      if (!target) { setError(first.msg); return; }

      target.classList.add('hxset__gap-target');
      // Prompt message: append near the label (option) or into the block label.
      var host = target.querySelector('.product-variants-block__label') ||
                 target.querySelector('.hxset__block-label') || target;
      var msg = host.querySelector('.hxset__prompt-msg');
      if (!msg) {
        msg = el('span', 'hxset__prompt-msg');
        host.appendChild(msg);
      }
      msg.textContent = first.msg;

      // Restart the pulse on repeat clicks.
      target.classList.remove('is-prompt');
      void target.offsetWidth;
      target.classList.add('is-prompt');

      var rect = target.getBoundingClientRect();
      if (rect.top < 80 || rect.bottom > window.innerHeight - 80) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setError('');
    }

    function addSet(opts) {
      if (adding) return;
      var lines = [];
      var complete = activeSlug != null;
      order.forEach(function (key) {
        var piece = pieces[key];
        var v = (piece.product && piece.chosen) ? resolveVariant(piece.product, piece.chosen) : null;
        if (v) lines.push({ id: v.id, quantity: 1 });
        else complete = false;
      });

      if (!complete || lines.length !== order.length) {
        promptFirstGap();
        return;
      }

      // Size-chart fit prompt (size-chart.js): after the selection gate — the
      // set is fully picked — ask once per session if neither piece's family
      // chart was viewed. gateSet opens the prompt and returns true; "Add
      // anyway" re-enters here with skipSizeGate so we don't loop.
      if (!(opts && opts.skipSizeGate) &&
          window.HealixSizeChart && typeof window.HealixSizeChart.gateSet === 'function') {
        var gated = window.HealixSizeChart.gateSet(root, function () {
          addSet({ skipSizeGate: true });
        });
        if (gated) return;
      }

      adding = true;
      setError('');
      clearAllPrompts();
      updateState();
      var restore = dom.addText ? dom.addText.textContent : '';
      if (dom.addText) dom.addText.textContent = 'Adding…';

      var post = (window.CartAPI && typeof window.CartAPI.addItems === 'function')
        ? window.CartAPI.addItems(lines)
        : fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: lines })
          }).then(function (r) { if (!r.ok) return r.json().then(function (d) { throw new Error(d.description || d.message || 'Add failed'); }); return r.json(); });

      Promise.resolve(post).then(function () {
        adding = false;
        if (dom.addText) dom.addText.textContent = 'Added to bag ✓';
        updateState();
        setTimeout(function () { if (dom.addText) dom.addText.textContent = restore || addLabel; }, 1800);
      }).catch(function (e) {
        adding = false;
        if (dom.addText) dom.addText.textContent = restore || addLabel;
        setError((e && e.message) || 'Could not add the set to your bag. Please try again.');
        updateState();
      });
    }

    /* ---- boot ---------------------------------------------------------- */

    order.forEach(function (key) {
      var piece = pieces[key];
      if (piece.pickersHost) renderPieceEmpty(piece);
    });
    if (dom.add) dom.add.addEventListener('click', addSet);
    updateState();

    Promise.all([
      window.HealixColors.siblingsRaw(topHandle),
      window.HealixColors.siblingsRaw(pantHandle)
    ]).then(function (res) {
      var topSib = res[0], pantSib = res[1];
      if (!topSib || !pantSib) { setError('Colours are unavailable right now. Please refresh.'); return; }
      shared = buildShared(topSib, pantSib);
      if (!shared.length) {
        setError('This set has no shared colours available.');
        if (dom.colorSkeleton) dom.colorSkeleton.hidden = true;
        return;
      }
      renderColorStrip();
      paintIcons(root);

      // Deep link: if ?color=<slug> names a shared colour, open on it; else fall
      // back to shared[0] silently (stale/invalid links degrade gracefully).
      var wantSlug = shared[0].slug;
      var wantColor = bootParams[COLOR_PARAM];
      if (wantColor) {
        var want = String(wantColor).toLowerCase();
        for (var i = 0; i < shared.length; i++) {
          if (shared[i].slug === want) { wantSlug = shared[i].slug; break; }
        }
      }
      urlSyncArmed = true; // arm URL reflection now that the colour strip exists
      selectColor(wantSlug);
    }).catch(function () {
      setError('Something went wrong loading this set. Please refresh.');
      if (dom.colorSkeleton) dom.colorSkeleton.hidden = true;
    });
  }

  function boot() {
    document.querySelectorAll('[data-hxset]').forEach(initSet);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) {
    var r = e.target && e.target.querySelector ? e.target.querySelector('[data-hxset]') : null;
    if (r) { r.dataset.hxsetReady = ''; initSet(r); }
  });

  window.HealixSet = { init: initSet };
})();
